import { Box, HStack, Icon, VStack, Text } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { getDataTracking } from '../../../utils';
import {
  CloudDownloadHorizontal,
  CloudDownloadSquare,
  CloudDownloadVertical,
  CloudDownloadHorizontalHover,
  CloudDownloadSquareHover,
  CloudDownloadVerticalHover,
} from '../../../assets';

type ImageShape = 'Vertical' | 'Horizontal' | 'Square';

/**
 * @description Builds a user friendly download name for a file
 * @param url string
 * @param altName string
 * @param imageShape string
 */
const getDownloadName = ({
  url,
  title,
  imageShape,
}: {
  url: string;
  title: string;
  imageShape: ImageShape;
}): string => {
  const extension = url.split('.').pop();
  return `${title.replace(/\s/g, '-')}-${imageShape}.${extension}`;
};

/**
 * @description
 * @prop
 */
export interface DownloadSectionProps {
  imageShape: ImageShape;
  title: string;
  imageUrl: string;
  socialApps: string[];
}

const getCloudIcon = (hover: boolean, imageShape: ImageShape): any => {
  if (imageShape === 'Vertical') {
    return hover ? CloudDownloadVerticalHover : CloudDownloadVertical;
  }
  if (imageShape === 'Horizontal') {
    return hover ? CloudDownloadHorizontalHover : CloudDownloadHorizontal;
  }
  if (imageShape === 'Square') {
    return hover ? CloudDownloadSquareHover : CloudDownloadSquare;
  }

  // Should never reach here, provide some default
  return CloudDownloadVertical;
};

export const DownloadSection: FC<DownloadSectionProps> = ({
  imageShape,
  title,
  imageUrl,
  socialApps,
}): JSX.Element => {
  const [hover, setHover] = useState(false);

  return (
    <Box
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      w="100%"
      textDecoration="none"
      bgColor={hover ? 'brand.additional.link' : 'white'}
      borderRadius="4px"
    >
      <a
        style={{ textDecoration: 'none' }}
        {...getDataTracking(`sharable-download-${imageShape}-${title}`)}
        href={imageUrl}
        download={getDownloadName({
          url: imageUrl,
          imageShape,
          title,
        })}
      >
        <VStack
          _hover={{
            color: 'white',
          }}
          borderWidth="2px"
          borderRadius="4px"
          cursor="pointer"
          padding="1rem"
          spacing={3}
        >
          <HStack spacing={3}>
            <Icon
              as={getCloudIcon(hover, imageShape)}
              color={hover ? 'white' : 'brand.additional.link'}
              boxSize="30px"
            />
            <Text
              color={hover ? 'white' : 'brand.additional.link'}
              fontWeight="bold"
            >
              {`Download ${imageShape}`}
            </Text>
          </HStack>
          <VStack spacing={1}>
            {socialApps.map((app, index) => (
              <Box key={index}>{app}</Box>
            ))}
          </VStack>
        </VStack>
      </a>
    </Box>
  );
};
